import React, { useState } from "react";
import {
    Gamepad,
    Group,
    Language,
    OpenInNew,
    Wifi,
    Key
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Collapse,
    Grid,
    Badge as MuiBadge,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import { motion } from "framer-motion";
import ConnectionStatus from "./ConnectionStatus";
import { useAuth } from "./lichess/LichessAuth";

export default function Chess() {
    const theme = useTheme();
    const { me, login, loginWithToken, logout, isLoading, error, clearError } = useAuth();
    const [showTokenInput, setShowTokenInput] = useState(false);
    const [patToken, setPatToken] = useState("");
    const [isSubmittingPat, setIsSubmittingPat] = useState(false);

    const handlePatSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!patToken.trim()) return;
        setIsSubmittingPat(true);
        const success = await loginWithToken(patToken);
        setIsSubmittingPat(false);
        if (success) {
            setPatToken("");
            setShowTokenInput(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "background.default",
                p: { xs: 2, md: 6 },
            }}
        >
            <Box maxWidth="1200px" mx="auto" display="flex" flexDirection="column" gap={2}>
                {/* Header */}
                <Box
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Gamepad sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                        <Typography variant="h3" fontWeight="bold" color="text.primary">
                            Chess Connect
                        </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: { xs: 2, md: 0 } }}>
                        Connect to online chess platforms and sync your games
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {me ? (
                    <ConnectionStatus me={me} />
                ) : (
                    <Wifi sx={{ color: theme.palette.text.disabled }} />
                )}

                {/* Chess Platforms Card */}
                <Card>
                    <CardHeader
                        title={
                            <Typography variant="h5" fontWeight="bold" color="text.primary">
                                Available Platforms
                            </Typography>
                        }
                    />
                    <CardContent>
                        <Grid container spacing={4}>
                            <Grid
                                size={{ xs: 12, md: 6 }}
                                component={motion.div}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1 * 0.1 }}
                            >
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: me ? "primary.main" : "divider",
                                        bgcolor: me ? "primary.dark" : "background.paper",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        "&:hover": {
                                            bgcolor: me ? "primary.dark" : "action.hover",
                                        },
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                                            Lichess
                                        </Typography>
                                        <MuiBadge
                                            badgeContent="50M+"
                                            color="secondary"
                                            sx={{
                                                bgcolor: `blue.900`,
                                                color: `blue.400`,
                                                border: 1,
                                                borderColor: `blue.800`,
                                                fontWeight: "bold",
                                            }}
                                        />
                                    </Box>
                                    <Typography color="text.secondary" mb={2}>
                                        Free and open-source chess server
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap={1} color="text.secondary">
                                        <Box display="flex" alignItems="center" gap={1} fontSize="0.875rem">
                                            <Language fontSize="small" />
                                            <Typography component="span">lichess.org</Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1} fontSize="0.875rem">
                                            <Group fontSize="small" />
                                            <Typography component="span">50M+ players</Typography>
                                        </Box>
                                    </Box>

                                    <Box mt="auto" display="flex" flexDirection="column" gap={1} pt={2}>
                                        <Box display="flex" gap={1}>
                                            <Button
                                                onClick={() => (me ? logout() : login())}
                                                variant="contained"
                                                fullWidth
                                                disabled={isLoading}
                                                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                                                sx={{ textTransform: "none" }}
                                            >
                                                {isLoading ? "Connecting..." : me ? "Disconnect" : "Connect with Lichess"}
                                            </Button>
                                            <Tooltip title="Open Lichess website" arrow>
                                                <Button
                                                    variant="outlined"
                                                    color="inherit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open("https://lichess.org/", "_blank");
                                                    }}
                                                >
                                                    <OpenInNew />
                                                </Button>
                                            </Tooltip>
                                        </Box>

                                        {!me && (
                                            <>
                                                <Button
                                                    size="small"
                                                    color="secondary"
                                                    startIcon={<Key fontSize="small" />}
                                                    onClick={() => setShowTokenInput((prev) => !prev)}
                                                    sx={{ textTransform: "none", alignSelf: "flex-start", mt: 1 }}
                                                >
                                                    {showTokenInput ? "Hide API Token Login" : "Or use Personal API Token"}
                                                </Button>

                                                <Collapse in={showTokenInput}>
                                                    <Box component="form" onSubmit={handlePatSubmit} sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                                                        <TextField
                                                            size="small"
                                                            type="password"
                                                            label="Lichess API Token"
                                                            placeholder="lip_..."
                                                            value={patToken}
                                                            onChange={(e) => setPatToken(e.target.value)}
                                                            fullWidth
                                                            helperText="Generate at lichess.org/account/oauth/token with 'board:play' scope."
                                                        />
                                                        <Button
                                                            type="submit"
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={isSubmittingPat || !patToken.trim()}
                                                            startIcon={isSubmittingPat ? <CircularProgress size={16} color="inherit" /> : undefined}
                                                            sx={{ textTransform: "none", alignSelf: "flex-start" }}
                                                        >
                                                            {isSubmittingPat ? "Validating..." : "Connect with Token"}
                                                        </Button>
                                                    </Box>
                                                </Collapse>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid
                                size={{ xs: 12, md: 6 }}
                                component={motion.div}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 2 * 0.1 }}
                            >
                                <Box
                                    sx={{
                                        p: 3,
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: "divider",
                                        bgcolor: "background.paper",
                                        display: "flex",
                                        flexDirection: "column",
                                        height: "100%",
                                        "&:hover": {
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                                            Upcoming
                                        </Typography>
                                        <MuiBadge
                                            badgeContent="-"
                                            color="secondary"
                                            sx={{
                                                bgcolor: `blue.900`,
                                                color: `blue.400`,
                                                border: 1,
                                                borderColor: `blue.800`,
                                                fontWeight: "bold",
                                            }}
                                        />
                                    </Box>
                                    <Typography color="text.secondary" mb={2}>
                                        More to come
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gap={1} color="text.secondary">
                                        <Box display="flex" alignItems="center" gap={1} fontSize="0.875rem">
                                            <Language fontSize="small" />
                                            <Typography component="span">-</Typography>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1} fontSize="0.875rem">
                                            <Group fontSize="small" />
                                            <Typography component="span">-</Typography>
                                        </Box>
                                    </Box>

                                    <Box mt="auto" display="flex" gap={1} pt={2}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            sx={{ textTransform: "none" }}
                                            disabled={true}
                                        >
                                            -
                                        </Button>
                                        <Tooltip title="-" arrow>
                                            <Button
                                                disabled={true}
                                                variant="outlined"
                                                color="inherit"
                                            >
                                                <OpenInNew />
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
